"use client"

import clsx from "clsx"
import React, { useEffect, useState } from "react"
import { addComma, getByteSize, isEmpty } from "../../utils"

/**
 * @description 글자수/바이트수 카운터. "현재/최대" 형식으로 표시
 * @example
 * ```tsx
 * <TCounter value={inputValue} maxChar={200} />
 * <TCounter value={inputValue} maxByte={500} onLengthExceeded={setExceeded} />
 * ```
 */
interface TCounterProps {
	/** 현재 입력값 */
	value?: string
	/** 최대 글자수 제한 */
	maxChar?: number
	/** 최대 바이트수 제한 (한글 3byte) */
	maxByte?: number
	/** 비활성화 여부 */
	disabled?: boolean
	/** 에러 상태 표시 */
	error?: boolean
	/** 경고 상태 표시 */
	warning?: boolean
	/** 추가 CSS 클래스 */
	className?: string
	/** 제한 초과 시 true 전달, 이내일 때 false 전달 */
	onLengthExceeded?: (value: boolean) => void
}

export const TCounter = React.memo((props: TCounterProps) => {
	const { value, maxChar, maxByte, disabled, error, warning, className, onLengthExceeded } = props
	const [updateValue, setUpdateValue] = useState<string>(value ?? "")
	const [charCount, setCharCount] = useState<number>(0)
	const [byteCount, setByteCount] = useState<number>(0)
	const [isLengthExceeded, setIsLengthExceeded] = useState<boolean>(false)

	const displayError = React.useMemo(() => isLengthExceeded || error, [isLengthExceeded, error])

	useEffect(() => {
		setUpdateValue(value ?? "")
	}, [value])

	useEffect(() => {
		const currentValue = updateValue || ""
		const currentLength = currentValue.length
		setCharCount(currentLength)

		const currentBytes = isEmpty(currentValue) ? 0 : getByteSize(currentValue)
		setByteCount(currentBytes)

		let exceeded = false
		if (maxChar !== undefined && currentLength > maxChar) {
			exceeded = true
		}
		if (!exceeded && maxByte !== undefined && currentBytes > maxByte) {
			exceeded = true
		}
		setIsLengthExceeded(exceeded)
		if (onLengthExceeded) {
			onLengthExceeded(exceeded)
		}
	}, [updateValue, maxChar, maxByte, onLengthExceeded])

	const counterData = React.useMemo(() => {
		if (maxChar === undefined && maxByte === undefined) {
			return null
		}
		const count = maxByte !== undefined ? byteCount : charCount
		const limit = maxByte !== undefined ? maxByte : maxChar!
		return { count, limit }
	}, [maxChar, maxByte, byteCount, charCount])

	if (!counterData) {
		return null
	}

	return (
		<span className={clsx("TCounter", { disabled }, className)}>
			<span className={clsx({ error: displayError && !disabled, warning: warning && !disabled })}>{addComma(counterData.count)}</span>/{addComma(counterData.limit)}
		</span>
	)
})

TCounter.displayName = "TCounter"
